import type { Setter } from 'jotai';
import { atom } from 'jotai';
import { v4 as uuid } from 'uuid';
import { isFrameClassificationLabel } from '../../../../common/side-panel/utils/utils';
import { TASK_MODE } from '../../../../common/task-state/constants';
import { currentThemeAtom } from '../../../../common/theme/theme-atoms';
import { STORE_TYPE } from '../../../../common/types/types';
import { getRandomId, insertIf } from '../../../../common/utils/utils';
import { undoRedoAtoms } from '../../../../modernization-common/undo-redo/undo-redo';
import { KEY_FRAME_TYPE } from '../../../labelling/components/frames-control-panel/constants';
import { ANNOTATION_TYPE, TOOL } from '../../../labelling/constants';
import { GRID_MODE_ORDER } from '../../../labelling/constants/index';
import { type ActionT } from '../../../labelling/reducers/actions';
import { getAvailableTools, getSelectedTool, getToolsForAnnotation } from '../../../labelling/reducers/utils/tool';
import { filterDynamicAnswers as excludeDynamicAnswers, filterStaticAnswers as excludeStaticAnswers, handleLockAnnotationFromAnnotationStore, handleLockAnnotationLabelFromAnnotationStore, handleShowHideAllAnnotations, handleShowHideAnnotationFromAnnotationStore, handleShowHideAnnotationLabelFromAnnotationStore } from '../../../labelling/reducers/utils/utils';
import { EDITOR_MODE_TYPE } from '../../../labelling/types';
import { createKeyFrame } from '../../../labelling/utils';
import { type ActionT as ModernisedAction, ACTION as MODERNISED_ACTION } from '../../actions';
import { computeTargetFrameRangeLimits } from '../../components/relationship-component/utils';
import { removeDynamicAnswerSegments, updateDynamicAnswerSegments } from '../../studio-store/utils/dynamic-answer-utils';
import { applyAnnotationUIState } from '../../utils/apply-annotation-ui-state';
import { getM4OverriddenLabelTitles } from '../../utils/apply-m4-label-colors';
import { buildAnnotationFrameMap } from '../../utils/build-annotation-frame-map';
import { collectAllAnnotationIds, collectIdsByLabel, collectIdsByLabelFromMLStore, collectIdsByLabelTitles, collectIdsByLabelTitlesFromMLStore } from '../../utils/collect-ids-from-annotation-metadata';
import { annotationFramesAtom, annotationIdToLabelMapAtom, annotationLabelsAtom, annotationMetadataAtom, annotationUIStateAtom, annotationUnderUserReviewAtom, consensusAnnotationFramesAtom, currentFrameLoadedAnnotationsAtom, frameCountAtom, isVideoTaskAtom, mlAnnotationFramesAtom, mlAnnotationUIStateAtom, overriddenM4LabelColorTitlesAtom } from '../annotation-atoms';
import { metadataAtom } from '../file-atoms';
import { frameImageUrlsAtom } from '../frame-image-urls-atom';
import { annotationPropagationMethod } from '../frame-timeline-atoms';
import { isGridViewEnabledAtom } from '../grid-view-atoms';
import { imageSettingsAtom } from '../image-settings-atoms';
import { isVideoLabelingM4EnabledAtom } from '../m4-atoms';
import { activeDraftIdAtom, allAnnotationsDataWithFrameRangesAtom, draftRelationshipsAtom, selectedLabelNameAtom, sharedFrameRangeAtom, sourceObjectIdAtom } from '../relationship-atoms';
import { annotationIdToSpanAtom, sidePanelAtomDispatch } from '../side-panel-atoms';
import { editorModeAtom } from '../stage-atoms';
import { type AnnotationFrameWriteEntry, STUDIO_WRITE_ACTIONS } from '../studio-store-write/actions';
import { dispatchStudioStoreWriteAtom } from '../studio-store-write/dispatch-studio-store-write';
import { taskModeAtom } from '../task-mode-atoms';
import { expandAnnotationIdsAtom } from '../timeline-expansion-atoms';
import { brushToolSettingsAtom, copiedAnnotationIdAtom, draftFrameClassificationAtom, focusedAnnotationIdAtom, gridModeAtom, isLoadingAtom, multiSelectEnabledAtom, selectedAnnotationIdsAtom, selectedAnnotationLabelAtom, selectedSubToolAtom, selectedToolAtom, showAnnotationDetailsAtom, snapEnabledAtom } from '../tool-atoms';
import { currentFrameAtom, debouncedCurrentFrame, hoveredFrameAtom } from '../video-playback-atoms';
import { commandConfig } from './commands/command-config';
import { type StudioEvent, type UndoableEventPayloads, EVENTS, UNDOABLE_EVENTS } from './events';
type MergedAction = ActionT | ModernisedAction;
const applyUIStateForStoreType = (set: Setter, storeType: (typeof STORE_TYPE)[keyof typeof STORE_TYPE] | undefined, ids: string[], partial: Parameters<typeof applyAnnotationUIState>[2]) => {
  const targetAtom = storeType === STORE_TYPE.ML_ANNOTATION_STORE ? mlAnnotationUIStateAtom : annotationUIStateAtom;
  set(targetAtom, prev => applyAnnotationUIState(prev, ids, partial));
};
const {
  dispatch: dispatchUndoRedoAtom,
  canUndo: canUndoAtom,
  canRedo: canRedoAtom
} = undoRedoAtoms<void, UndoableEventPayloads>(commandConfig);
export { canUndoAtom, canRedoAtom };
export const dispatchAtom = atom(undefined, (get, set, action: MergedAction | StudioEvent) => {
  const isUndoableAction = Object.keys(commandConfig).includes(action.type);
  if (isUndoableAction) {
    return set(dispatchUndoRedoAtom, action);
  }
  switch (action.type) {
    case 'UNDO':
    case 'REDO':
      return set(dispatchUndoRedoAtom, action);
    case EVENTS.SELECT_ANNOTATION_LABEL:
      {
        const annotationLabel = action.annotationLabel;
        if (annotationLabel === null) {
          set(selectedToolAtom, TOOL.POINTER);
          set(selectedAnnotationIdsAtom, []);
          set(selectedAnnotationLabelAtom, undefined);
          set(copiedAnnotationIdAtom, null);
          return;
        }
        const isFrameClassificationType = isFrameClassificationLabel(annotationLabel);
        if (!isFrameClassificationType) {
          const tools = getAvailableTools(annotationLabel);
          const newSelectedTool = getSelectedTool(get(selectedToolAtom), tools);
          set(selectedToolAtom, newSelectedTool);
          set(selectedAnnotationIdsAtom, []);
          set(selectedAnnotationLabelAtom, annotationLabel);
          set(copiedAnnotationIdAtom, null);
          return;
        }
        if (get(isVideoLabelingM4EnabledAtom)) {
          const labelMap = get(annotationIdToLabelMapAtom);
          const existingAnnotationIdx = [...labelMap.entries()].find(([, label]) => label.title === annotationLabel.title)?.[0];
          if (existingAnnotationIdx) {
            set(draftFrameClassificationAtom, null);
            set(copiedAnnotationIdAtom, null);
            set(selectedAnnotationIdsAtom, [existingAnnotationIdx]);
            set(showAnnotationDetailsAtom, true);
            return;
          }
          const copiedAnnotationId = get(copiedAnnotationIdAtom);
          set(copiedAnnotationIdAtom, null);
          const draftAnnotationId = copiedAnnotationId ?? getRandomId();
          set(draftFrameClassificationAtom, {
            annotationId: draftAnnotationId,
            annotationLabel
          });
          set(selectedAnnotationIdsAtom, [draftAnnotationId]);
          set(showAnnotationDetailsAtom, true);
          return;
        }
        const currentFrameAnnotations = get(currentFrameLoadedAnnotationsAtom);
        const labelMap = get(annotationIdToLabelMapAtom);
        const isAnnotationAlreadyPresent = [...currentFrameAnnotations.keys()].some(annotationId => labelMap.get(annotationId)?.title === annotationLabel.title);
        if (!isAnnotationAlreadyPresent) {
          const copiedAnnotationId = get(copiedAnnotationIdAtom);
          const copiedAnnotationMetadata = copiedAnnotationId ? get(annotationMetadataAtom).get(copiedAnnotationId) : undefined;
          const annotationIdx = copiedAnnotationId ?? getRandomId();
          set(dispatchUndoRedoAtom, {
            type: UNDOABLE_EVENTS.ADD_ANNOTATION,
            frameIndex: get(currentFrameAtom),
            annotationIdx,
            annotationData: {
              annotationType: ANNOTATION_TYPE.FRAME_CLASSIFICATION,
              annotationLabel,
              keyFrame: createKeyFrame(true),
              data: null,
              metadata: copiedAnnotationMetadata ? {
                answers: copiedAnnotationMetadata.answers
              } : undefined
            }
          });
        }
        set(copiedAnnotationIdAtom, null);
        return;
      }
    case MODERNISED_ACTION.TOGGLE_VIDEO_GRID:
      {
        set(isGridViewEnabledAtom, !get(isGridViewEnabledAtom));
        return;
      }
    case EVENTS.SET_IMAGE_SETTINGS:
      {
        set(imageSettingsAtom, action.imageSettings);
        return;
      }
    case EVENTS.SELECT_ANNOTATION:
      {
        const isReviewMode = get(taskModeAtom) === TASK_MODE.REVIEW;

        // Review mode guard: prevent deselecting annotations during review.
        if (isReviewMode && !action.annotationId) return;
        const multiSelectEnabled = get(multiSelectEnabledAtom);
        const selectedAnnotationIds = get(selectedAnnotationIdsAtom);
        const showAnnotationDetails = get(showAnnotationDetailsAtom);
        const editorMode = get(editorModeAtom);
        const {
          annotationId
        } = action;
        if (annotationId === undefined || annotationId === null) {
          set(selectedAnnotationIdsAtom, multiSelectEnabled ? selectedAnnotationIds : []);
          set(showAnnotationDetailsAtom, false);
          return;
        }
        let updatedSelectedAnnotationIds = [...selectedAnnotationIds];
        const annotationIdx = updatedSelectedAnnotationIds.indexOf(annotationId);
        if (multiSelectEnabled) {
          if (annotationIdx >= 0) {
            updatedSelectedAnnotationIds.splice(annotationIdx, 1);
          } else {
            updatedSelectedAnnotationIds.push(annotationId);
          }
        } else {
          updatedSelectedAnnotationIds = [annotationId];
        }
        set(selectedAnnotationIdsAtom, updatedSelectedAnnotationIds);
        // Expand selected rows in the same commit as the selection write. Doing this here (rather
        // than via a trailing effect) keeps the auto-scroll-to-selection smooth.
        set(expandAnnotationIdsAtom, updatedSelectedAnnotationIds);
        if (get(isVideoLabelingM4EnabledAtom)) {
          set(showAnnotationDetailsAtom, isReviewMode ? false : editorMode === EDITOR_MODE_TYPE.RELATIONSHIP ? showAnnotationDetails : updatedSelectedAnnotationIds.length === 1);
        }
        if (annotationId) {
          const sectionKey = get(annotationIdToLabelMapAtom).get(annotationId)?.title;
          set(sidePanelAtomDispatch, {
            type: 'SELECT_ANNOTATION',
            annotationId,
            sectionKey
          });
          if (isReviewMode) {
            set(sidePanelAtomDispatch, {
              type: 'RESET_VISIBILITY'
            });
          }
        }

        // Navigate to the annotation's frame if currentFrame is outside its ranges.
        const annotationIdToSpan = get(annotationIdToSpanAtom);
        const currentFrame = get(currentFrameAtom);
        const annotationSpan = annotationIdToSpan[annotationId];
        if (annotationSpan) {
          const isInRange = annotationSpan.some(range => currentFrame >= range.start && currentFrame <= range.end);
          if (!isInRange) {
            set(debouncedCurrentFrame, annotationSpan[0]?.start ?? currentFrame);
          }
        }
        return;
      }
    case EVENTS.FOCUS_ANNOTATION:
      {
        set(focusedAnnotationIdAtom, action.annotationId ?? undefined);
        return;
      }
    case EVENTS.TOGGLE_MULTI_SELECT:
      {
        set(multiSelectEnabledAtom, action.enabled);
        return;
      }
    case EVENTS.SELECT_RELATIONSHIP_SOURCE:
      {
        const {
          annotationId
        } = action;
        set(sourceObjectIdAtom, annotationId);
        const draftId = uuid();
        const newDrafts = {
          [draftId]: {
            id: draftId,
            targetId: '',
            frameRanges: [{}]
          }
        };
        set(draftRelationshipsAtom, newDrafts);
        set(activeDraftIdAtom, draftId);
        set(selectedLabelNameAtom, '');
        set(sharedFrameRangeAtom, {});
        if (annotationId && get(isVideoTaskAtom)) {
          const sourceEntry = get(allAnnotationsDataWithFrameRangesAtom)[annotationId];
          const currentFrame = get(currentFrameAtom);
          const inRange = sourceEntry?.frameRanges.some(({
            startFrameIndex,
            endFrameIndex
          }) => currentFrame >= startFrameIndex && currentFrame <= endFrameIndex);
          if (!inRange && sourceEntry?.frameRanges[0]) {
            set(debouncedCurrentFrame, sourceEntry.frameRanges[0].startFrameIndex);
          }
        }
        return;
      }
    case EVENTS.SELECT_RELATIONSHIP_TARGET:
      {
        const {
          annotationId
        } = action;
        const sourceId = get(sourceObjectIdAtom);
        if (!annotationId || annotationId === sourceId) return;
        const allAnnotations = get(allAnnotationsDataWithFrameRangesAtom);
        const sourceEntry = allAnnotations[sourceId];
        const targetEntry = allAnnotations[annotationId];
        if (!sourceEntry || !targetEntry) return;
        const activeDraftId = get(activeDraftIdAtom);
        set(draftRelationshipsAtom, prev => {
          const draft = prev[activeDraftId];
          if (!draft) return prev;
          const {
            frameRanges
          } = computeTargetFrameRangeLimits(sourceEntry.frameRanges, targetEntry.frameRanges);
          return {
            ...prev,
            [activeDraftId]: {
              ...draft,
              targetId: annotationId,
              frameRanges: frameRanges.slice(0, 1)
            }
          };
        });
        return;
      }
    case EVENTS.SELECT_TOOL:
      {
        set(selectedToolAtom, action.tool);
        set(selectedSubToolAtom, action.subTool);
        set(copiedAnnotationIdAtom, null);
        return;
      }
    case EVENTS.SET_HOVERED_FRAME:
      {
        set(hoveredFrameAtom, action.frameId);
        return;
      }
    case EVENTS.SET_IMAGE_META_DATA:
      {
        set(metadataAtom, prev => ({
          ...prev,
          ...action.metaData
        }));
        return;
      }
    case EVENTS.TOGGLE_SNAP:
      {
        set(snapEnabledAtom, !get(snapEnabledAtom));
        return;
      }
    case EVENTS.TOGGLE_GRID_MODE:
      {
        const currentGridMode = get(gridModeAtom);
        const currentGridModeIndex = GRID_MODE_ORDER.indexOf(currentGridMode);
        const newGridModeIndex = (currentGridModeIndex + 1) % GRID_MODE_ORDER.length;
        const newGridMode = GRID_MODE_ORDER[newGridModeIndex];
        if (!newGridMode) {
          return;
        }
        set(gridModeAtom, newGridMode);
        return;
      }
    case EVENTS.SET_CURRENT_FRAME:
      {
        set(debouncedCurrentFrame, action.frameId);
        return;
      }
    case EVENTS.SET_BRUSH_TOOL_SETTINGS:
      {
        set(brushToolSettingsAtom, action.brushToolSettings);
        return;
      }
    case EVENTS.HIDE_ANNOTATION_DETAILS_PANEL:
      {
        set(showAnnotationDetailsAtom, false);
        set(draftFrameClassificationAtom, null);
        return;
      }
    case EVENTS.OPEN_ANNOTATION_DETAILS_PANEL:
      {
        set(showAnnotationDetailsAtom, true);
        return;
      }
    case EVENTS.SET_LOADING:
      {
        set(isLoadingAtom, action.isLoading);
        return;
      }
    case EVENTS.UPDATE_ANNOTATION_PROPAGATION_METHOD:
      {
        set(annotationPropagationMethod, action.propagationMethod);
        return;
      }
    case EVENTS.RESET_ANNOTATION_LABEL:
      {
        set(selectedAnnotationLabelAtom, undefined);
        return;
      }
    case EVENTS.TOGGLE_ANNOTATION_RELATIONSHIP_VIEW:
      {
        set(editorModeAtom, action.editorMode);
        if (action.editorMode === EDITOR_MODE_TYPE.RELATIONSHIP) {
          set(selectedToolAtom, TOOL.POINTER);
        }
        return;
      }
    case EVENTS.CREATE_VIDEO_FRAMES:
      {
        set(annotationFramesAtom, action.frames);
        set(mlAnnotationFramesAtom, buildAnnotationFrameMap(action.frames, 'annotationMLStore'));
        set(consensusAnnotationFramesAtom, buildAnnotationFrameMap(action.frames, 'annotationConsensusStore'));
        return;
      }
    case EVENTS.SET_FRAME_COUNT:
      {
        set(frameCountAtom, action.frameCount);
        return;
      }
    case EVENTS.UPDATE_ALL_ANNOTATION_LABELS:
      {
        set(annotationLabelsAtom, action.annotationLabels);
        set(overriddenM4LabelColorTitlesAtom, getM4OverriddenLabelTitles(action.annotationLabels, get(currentThemeAtom)));
        return;
      }
    case EVENTS.UPDATE_ANNOTATION_LABELS:
      {
        const annotationLabels = [...get(annotationLabelsAtom)];
        const labelIndex = annotationLabels.findIndex(label => label.title === action.title);
        if (labelIndex >= 0 && annotationLabels[labelIndex]) {
          annotationLabels[labelIndex] = {
            ...annotationLabels[labelIndex],
            style: {
              ...annotationLabels[labelIndex].style,
              fillColor: action.color,
              strokeColor: action.color
            }
          };
        }
        set(annotationLabelsAtom, annotationLabels);
        set(overriddenM4LabelColorTitlesAtom, new Set(get(overriddenM4LabelColorTitlesAtom)).add(action.title));
        return;
      }
    case EVENTS.COPY_ANNOTATION_ID:
      {
        const {
          annotationId
        } = action;
        const metadata = get(annotationMetadataAtom).get(annotationId);
        if (!metadata) {
          return;
        }
        set(copiedAnnotationIdAtom, annotationId);
        set(selectedToolAtom, getToolsForAnnotation(metadata.annotationType));
        const label = get(annotationIdToLabelMapAtom).get(annotationId);
        if (label) {
          set(selectedAnnotationLabelAtom, label);
        }
        return;
      }
    case EVENTS.LOCK_ANNOTATIONS:
      {
        let frames = [...get(annotationFramesAtom)];
        let selectedAnnotationIds = [...get(selectedAnnotationIdsAtom)];
        if (action.annotationLabel) {
          [frames, selectedAnnotationIds] = handleLockAnnotationLabelFromAnnotationStore(action.annotationLabel, action.locked, action.storeType, frames, selectedAnnotationIds);
          const labelId = action.annotationLabel.title;
          const selectedIds = action.storeType === STORE_TYPE.ML_ANNOTATION_STORE ? collectIdsByLabelFromMLStore(get(mlAnnotationFramesAtom), labelId) : collectIdsByLabel(get(annotationMetadataAtom), labelId);
          applyUIStateForStoreType(set, action.storeType, selectedIds, {
            locked: action.locked
          });
        } else if (action.annotationId) {
          [frames, selectedAnnotationIds] = handleLockAnnotationFromAnnotationStore(action.frameIndex, action.frameRange, action.annotationId, action.locked, action.storeType, frames, selectedAnnotationIds);
          applyUIStateForStoreType(set, action.storeType, [action.annotationId], {
            locked: action.locked
          });
        }
        set(annotationFramesAtom, frames);
        set(selectedAnnotationIdsAtom, selectedAnnotationIds);
        return;
      }
    case EVENTS.ADD_ANNOTATION_UNDER_REVIEW:
      {
        set(annotationUnderUserReviewAtom, {
          ...get(annotationUnderUserReviewAtom),
          ...action.annotations
        });
        set(isLoadingAtom, true);
        return;
      }
    case EVENTS.UPDATE_ANNOTATION_UNDER_REVIEW:
      {
        const currentReview = get(annotationUnderUserReviewAtom);
        const existing = currentReview[action.annotationId];
        if (!existing) return;
        set(annotationUnderUserReviewAtom, {
          ...currentReview,
          [action.annotationId]: Object.assign({}, existing, action.annotation)
        });
        set(isLoadingAtom, false);
        return;
      }
    case EVENTS.DELETE_ANNOTATION_UNDER_REVIEW:
      {
        const current = get(annotationUnderUserReviewAtom);
        const remaining = Object.fromEntries(Object.entries(current).filter(([id]) => !action.annotationIds.includes(id)));
        set(annotationUnderUserReviewAtom, remaining);
        set(isLoadingAtom, false);
        return;
      }
    case EVENTS.SHOW_HIDE_ANNOTATIONS:
      {
        let frames = [...get(annotationFramesAtom)];
        let idsToUpdate: string[] | null = null;
        if (action.annotationLabel) {
          frames = handleShowHideAnnotationLabelFromAnnotationStore(action.annotationLabel, action.hidden, action.storeType, frames);
          const labelId = action.annotationLabel;
          switch (action.storeType) {
            case undefined:
            case STORE_TYPE.ANNOTATION_STORE:
              {
                idsToUpdate = collectIdsByLabel(get(annotationMetadataAtom), labelId);
                break;
              }
            case STORE_TYPE.ML_ANNOTATION_STORE:
              {
                idsToUpdate = collectIdsByLabelFromMLStore(get(mlAnnotationFramesAtom), labelId);
                break;
              }
            case STORE_TYPE.CONSENSUS_ANNOTATION_STORE:
              {
                //NOT IMPLEMENTED - This is on-call fix only handling buggy case
                break;
              }
          }
        } else if (action.annotationId) {
          frames = handleShowHideAnnotationFromAnnotationStore(action.frameIndex, action.frameRange, action.annotationId, action.hidden, action.storeType, frames);
          idsToUpdate = [action.annotationId];
        } else if (action.allAnnotations) {
          frames = handleShowHideAllAnnotations(action.hidden, action.storeType, frames);
          idsToUpdate = collectAllAnnotationIds(get(annotationMetadataAtom));
        } else if (action.annotationLabels) {
          frames = action.annotationLabels.reduce((acc, annotationLabel) => handleShowHideAnnotationLabelFromAnnotationStore(annotationLabel.title, action.hidden, action.storeType, acc), frames);
          const labelTitles = action.annotationLabels.map(l => l.title);
          switch (action.storeType) {
            case undefined:
            case STORE_TYPE.ANNOTATION_STORE:
              {
                idsToUpdate = collectIdsByLabelTitles(get(annotationMetadataAtom), labelTitles);
                break;
              }
            case STORE_TYPE.ML_ANNOTATION_STORE:
              {
                idsToUpdate = collectIdsByLabelTitlesFromMLStore(get(mlAnnotationFramesAtom), labelTitles);
                break;
              }
            case STORE_TYPE.CONSENSUS_ANNOTATION_STORE:
              {
                //NOT IMPLEMENTED - This is on-call fix only handling buggy case
                break;
              }
          }
        }
        set(annotationFramesAtom, frames);
        if (idsToUpdate) {
          applyUIStateForStoreType(set, action.storeType, idsToUpdate, {
            hidden: action.hidden
          });
        }
        return;
      }
    case EVENTS.UPDATE_QUESTIONNAIRE:
      {
        const {
          annotationId,
          answers
        } = action;
        const frames = get(annotationFramesAtom);
        const updatedFrames = [...frames];
        let modified = false;
        for (let i = 0; i < updatedFrames.length; i++) {
          const frame = updatedFrames[i];
          if (!frame?.annotationStore[annotationId]) continue;
          updatedFrames[i] = {
            ...frame,
            annotationStore: {
              ...frame.annotationStore,
              [annotationId]: {
                ...frame.annotationStore[annotationId],
                metadata: {
                  ...frame.annotationStore[annotationId]?.metadata,
                  answers: {
                    // Preserve existing dynamic answers, replace static answers with new ones.
                    ...excludeStaticAnswers(frame.annotationStore[annotationId]?.metadata?.answers),
                    ...excludeDynamicAnswers(answers)
                  }
                }
              }
            }
          };
          modified = true;
        }
        if (modified) {
          set(annotationFramesAtom, updatedFrames);
        }
        const onlyStaticAnswers = excludeDynamicAnswers(answers);
        set(dispatchStudioStoreWriteAtom, {
          type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
          annotationId,
          partialPayload: {
            answers: onlyStaticAnswers
          }
        });
        return;
      }
    case EVENTS.UPDATE_DYNAMIC_QUESTIONNAIRE:
      {
        const {
          frameSpan,
          answers,
          annotationId
        } = action;
        const frames = [...get(annotationFramesAtom)];
        for (let index = frameSpan.start; index <= frameSpan.end; index++) {
          const frame = frames[index];
          if (frame?.annotationStore[annotationId]) {
            frames[index] = {
              ...frame,
              annotationStore: {
                ...frame.annotationStore,
                [annotationId]: {
                  ...frame.annotationStore[annotationId],
                  metadata: {
                    ...frame.annotationStore[annotationId]?.metadata,
                    answers: {
                      ...excludeStaticAnswers(frame.annotationStore[annotationId]?.metadata?.answers),
                      ...answers
                    }
                  }
                }
              }
            };
          }
        }
        set(annotationFramesAtom, frames);
        const onlyDynamicAnswers = excludeStaticAnswers(answers);
        const existingDynamicAnswers = get(annotationMetadataAtom).get(annotationId)?.dynamicAnswers ?? [];
        const updatedDynamicAnswers = updateDynamicAnswerSegments(existingDynamicAnswers, {
          frameSpan,
          answers: onlyDynamicAnswers
        });
        set(dispatchStudioStoreWriteAtom, {
          type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
          annotationId,
          partialPayload: {
            dynamicAnswers: updatedDynamicAnswers
          }
        });
        return;
      }
    case EVENTS.UPDATE_TRACKING_ANNOTATION_FRAMES:
      {
        const {
          annotationIdx,
          startFrame,
          endFrame,
          frameAnnotations
        } = action;
        const writes: AnnotationFrameWriteEntry[] = [];
        for (let frameIndex = startFrame; frameIndex <= endFrame; frameIndex++) {
          const annotation = frameAnnotations[frameIndex];
          if (annotation) {
            writes.push({
              frameIndex,
              annotationId: annotationIdx,
              annotation: {
                ...annotation,
                keyFrame: createKeyFrame(true, KEY_FRAME_TYPE.AUTOMATED)
              }
            });
          }
        }
        if (writes.length > 0) {
          set(dispatchStudioStoreWriteAtom, {
            type: STUDIO_WRITE_ACTIONS.BULK_WRITE,
            writes
          });
        }
        set(isLoadingAtom, false);
        return;
      }
    case EVENTS.REMOVE_DYNAMIC_ANSWERS:
      {
        const {
          annotationId,
          frameSpan
        } = action;
        const {
          startFrame,
          endFrame
        } = frameSpan;
        const frames = [...get(annotationFramesAtom)];
        for (let index = startFrame; index <= endFrame; index++) {
          const frame = frames[index];
          if (frame?.annotationStore[annotationId]) {
            const existingAnswers = frame.annotationStore[annotationId]?.metadata?.answers;
            frames[index] = {
              ...frame,
              annotationStore: {
                ...frame.annotationStore,
                [annotationId]: {
                  ...frame.annotationStore[annotationId],
                  metadata: {
                    ...insertIf(frame.annotationStore[annotationId]?.metadata, !!frame.annotationStore[annotationId]?.metadata),
                    answers: excludeDynamicAnswers(existingAnswers)
                  }
                }
              }
            };
          }
        }
        set(annotationFramesAtom, frames);
        const existingDynamicAnswers = get(annotationMetadataAtom).get(annotationId)?.dynamicAnswers ?? [];
        const updatedDynamicAnswers = removeDynamicAnswerSegments(existingDynamicAnswers, {
          start: startFrame,
          end: endFrame
        });
        set(dispatchStudioStoreWriteAtom, {
          type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
          annotationId,
          partialPayload: {
            dynamicAnswers: updatedDynamicAnswers
          }
        });
        return;
      }
    case EVENTS.SET_FRAME_IMAGE_URL:
      {
        const {
          frameIndex,
          imageUrl
        } = action;
        set(frameImageUrlsAtom, urls => urls.map((url, i) => i === frameIndex ? imageUrl : url));
        return;
      }
  }
});